import { Button, ButtonColors } from '@chiroup/components/Button';
import React, { useState } from 'react';
import {
  NEUROLOGICAL_ABBREVIATIONS,
  NEUROLOGICAL_CENTRAL_DEFAULTS,
  NEUROLOGICAL_CERVICAL_ROWS,
  NEUROLOGICAL_CRANIAL_DEFAULTS,
  NEUROLOGICAL_LOWER_ROWS,
  NEUROLOGICAL_LUMBAR_ROWS,
  NEUROLOGICAL_NOT_ASSESSED,
  NEUROLOGICAL_NO_DROPDOWN,
  NEUROLOGICAL_PERIPHERAL_OPTIONS,
  NEUROLOGICAL_PERIPHERAL_TD_OPTIONS,
  NEUROLOGICAL_SPINAL_OPTIONS,
  NEUROLOGICAL_TD_OPTIONS,
  NEUROLOGICAL_UPPER_ROWS,
  NEUROLOGICAL_WIDE_COLS,
} from '../../../../../constants';
import CentralEditableChart from './CentralEditableChart';
import CranialEditableChart from './CranialEditableChart';
import EditableChart from './EditableChart';
import NeurologicalRows from './NeurologicalRows';

const normalVals = (rowArr: string[], colArr: { text: string; normalDefaultValue: string }[]) => {
  return rowArr.reduce((obj: any, row: string) => {
    colArr.forEach(col => {
      if (
        !NEUROLOGICAL_NO_DROPDOWN?.includes(`${row}${col.text}`) &&
        !NEUROLOGICAL_NOT_ASSESSED.includes(`${row}${col.text}`)
      ) {
        obj[row] = {
          ...obj[row],
          [col.text]: {
            left: { choice: col.normalDefaultValue },
            right: { choice: col.normalDefaultValue },
          },
        };
      }
    });

    return obj;
  }, {});
};

const detailInfo: { [key in string]: any } = {
  cervical: normalVals(NEUROLOGICAL_CERVICAL_ROWS, NEUROLOGICAL_SPINAL_OPTIONS),
  lumbar: normalVals(NEUROLOGICAL_LUMBAR_ROWS, NEUROLOGICAL_SPINAL_OPTIONS),
  upper: normalVals(NEUROLOGICAL_UPPER_ROWS, NEUROLOGICAL_PERIPHERAL_OPTIONS),
  lower: normalVals(NEUROLOGICAL_LOWER_ROWS, NEUROLOGICAL_PERIPHERAL_OPTIONS),
  cranial: NEUROLOGICAL_CRANIAL_DEFAULTS,
  central: NEUROLOGICAL_CENTRAL_DEFAULTS,
  normal: {
    ...NEUROLOGICAL_CRANIAL_DEFAULTS,
    ...NEUROLOGICAL_CENTRAL_DEFAULTS,
  },
};

type Props = {
  close?: () => void;
  value: {
    [key: string]: any;
  };
  onChange: (
    region: string,
    prop: string,
    val: {
      value?: any;
    }
  ) => void;
  signed?: boolean;
};

const Neurological: React.FC<Props> = ({ close, value, onChange, signed }) => {
  const [editableCervicalChartIsOpen, setEditableCervicalChartIsOpen] = useState<boolean>(false);
  const [editableLumbarChartIsOpen, setEditableLumbarChartIsOpen] = useState<boolean>(false);
  const [editableUpperChartIsOpen, setEditableUpperChartIsOpen] = useState<boolean>(false);
  const [editableLowerChartIsOpen, setEditableLowerChartIsOpen] = useState<boolean>(false);
  const [editableCranialChartIsOpen, setEditableCranialChartIsOpen] = useState<boolean>(false);
  const [editableCentralChartIsOpen, setEditableCentralChartIsOpen] = useState<boolean>(false);

  const onChangeAllNormal = () => {
    const newValue = {
      cervical: {
        result: 'normal',
        detail: detailInfo.cervical,
      },
      lumbar: {
        result: 'normal',
        detail: detailInfo.lumbar,
      },
      upper: {
        result: 'normal',
        detail: detailInfo.upper,
      },
      lower: {
        result: 'normal',
        detail: detailInfo.lower,
      },
      cranial: { result: 'normal', detail: NEUROLOGICAL_CRANIAL_DEFAULTS },
      central: { result: 'normal', detail: NEUROLOGICAL_CENTRAL_DEFAULTS },
    };
    onChange('', '', { value: newValue });
  };

  const onChangeValue = (rowName: string, val: string) => {
    const newValue = { ...value };

    if (val === 'normal') {
      newValue[rowName] = {
        result: 'normal',
        detail: detailInfo[rowName],
      };
    } else if (val === 'abnormal') {
      newValue[rowName] = !newValue?.[rowName]?.detail
        ? {
            result: 'abnormal',
            detail: detailInfo[rowName],
          }
        : { ...newValue[rowName], result: 'abnormal' };
    } else if (val === 'not assessed') {
      delete newValue[rowName];
    }

    onChange('', '', { value: newValue });
  };

  return (
    <div className="w-full">
      <div className="p-4 grid grid-cols-4">
        <div className="col-span-1" />
        <button
          type="button"
          className="col-span-1 grid place-items-center rounded-md border  text-sm font-small transition duration-150 ease-in-out sm:mr-2  w-24 h-10  border-gray-300 bg-white text-gray-700 dark:bg-dark-gray-800 dark:text-dark-gray-100"
          onClick={onChangeAllNormal}
        >
          All Normal
        </button>
      </div>
      <div className="p-4 flex flex-col gap-4 ">
        <p className="truncate text-md font-medium text-gray-600 dark:text-dark-gray-100">Spinal</p>
        <NeurologicalRows
          subheading="Cervical"
          setChartOpen={setEditableCervicalChartIsOpen}
          onChange={onChangeValue}
          rowName="cervical"
          value={value}
        />
        <NeurologicalRows
          subheading="Lumbar"
          setChartOpen={setEditableLumbarChartIsOpen}
          onChange={onChangeValue}
          rowName="lumbar"
          value={value}
        />
      </div>
      <div className="p-4 flex flex-col gap-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <p className="truncate text-md font-medium text-gray-600 dark:text-dark-gray-100">
          Peripheral
        </p>
        <NeurologicalRows
          subheading="Upper"
          setChartOpen={setEditableUpperChartIsOpen}
          onChange={onChangeValue}
          rowName="upper"
          value={value}
        />
        <NeurologicalRows
          subheading="Lower"
          setChartOpen={setEditableLowerChartIsOpen}
          onChange={onChangeValue}
          rowName="lower"
          value={value}
        />
      </div>
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <NeurologicalRows
          heading="Cranial"
          onChange={onChangeValue}
          rowName="cranial"
          value={value}
          setChartOpen={setEditableCranialChartIsOpen}
        />
      </div>
      <div className="p-4 hover:bg-gray-50 dark:hover:bg-dark-gray-900">
        <NeurologicalRows
          heading="Central"
          onChange={onChangeValue}
          rowName="central"
          value={value}
          setChartOpen={setEditableCentralChartIsOpen}
        />
      </div>
      {!!close && (
        <div className="flex flex-row justify-end mt-4">
          <Button onClick={close} text="Close" color={ButtonColors.plainWithBorder} />
        </div>
      )}
      <EditableChart
        isOpen={editableCervicalChartIsOpen}
        close={() => setEditableCervicalChartIsOpen(false)}
        value={value?.cervical}
        colHeadings={NEUROLOGICAL_SPINAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_CERVICAL_ROWS}
        tdChoice={NEUROLOGICAL_TD_OPTIONS}
        title="Spinal | Cervical Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange('', '', { value: { ...value, cervical: val } });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="cervical"
        noDropdown={NEUROLOGICAL_NO_DROPDOWN}
        signed={signed}
      />
      <EditableChart
        isOpen={editableLumbarChartIsOpen}
        close={() => setEditableLumbarChartIsOpen(false)}
        value={value?.lumbar}
        colHeadings={NEUROLOGICAL_SPINAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_LUMBAR_ROWS}
        tdChoice={NEUROLOGICAL_TD_OPTIONS}
        title="Spinal | Lumbar Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange('', '', { value: { ...value, lumbar: val } });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="lumbar"
        noDropdown={NEUROLOGICAL_NO_DROPDOWN}
        signed={signed}
      />
      <EditableChart
        isOpen={editableUpperChartIsOpen}
        close={() => setEditableUpperChartIsOpen(false)}
        value={value?.upper}
        colHeadings={NEUROLOGICAL_PERIPHERAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_UPPER_ROWS}
        tdChoice={NEUROLOGICAL_PERIPHERAL_TD_OPTIONS}
        title="Peripheral | Upper Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange('', '', { value: { ...value, upper: val } });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="upper"
        signed={signed}
      />
      <EditableChart
        isOpen={editableLowerChartIsOpen}
        close={() => setEditableLowerChartIsOpen(false)}
        value={value?.lower}
        colHeadings={NEUROLOGICAL_PERIPHERAL_OPTIONS}
        rowHeadings={NEUROLOGICAL_LOWER_ROWS}
        tdChoice={NEUROLOGICAL_PERIPHERAL_TD_OPTIONS}
        title="Peripheral | Lower Neurologic Test"
        abbreviations={NEUROLOGICAL_ABBREVIATIONS}
        onChange={(val: any) => {
          onChange('', '', { value: { ...value, lower: val } });
        }}
        wideCols={NEUROLOGICAL_WIDE_COLS}
        test="lower"
        signed={signed}
      />
      <CranialEditableChart
        isOpen={editableCranialChartIsOpen}
        close={() => setEditableCranialChartIsOpen(false)}
        value={value?.cranial}
        onChange={(val: any) => {
          onChange('', '', { value: { ...value, cranial: val } });
        }}
        signed={signed}
      />
      <CentralEditableChart
        isOpen={editableCentralChartIsOpen}
        close={() => setEditableCentralChartIsOpen(false)}
        value={value?.central}
        onChange={(val: any) => {
          onChange('', '', { value: { ...value, central: val } });
        }}
        signed={signed}
      />
    </div>
  );
};

export default Neurological;
